'use client'

import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useParams } from 'react-router-dom'
import { 
  Shield, 
  Settings, 
  Globe, 
  Bot, 
  CheckCircle, 
  AlertCircle, 
  ExternalLink, 
  RefreshCw, 
  Key, 
  Lock, 
  Unlink,
  Link,
  Calendar,
  Clock,
  Target,
  Brain,
  Zap,
  Users,
  ThumbsUp
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/use-toast'

// Import Reddit service with generic types to avoid TypeScript errors
import { supabase } from '@/integrations/supabase/client'

interface RedditAccount {
  isConnected: boolean
  username?: string
  karma: number
  accountAge: string
  connectedAt?: string
  lastSync?: string
}

interface AgentSettings {
  enableRedditAgent: boolean
  requiresPostApproval: boolean
  autoSubscribe: boolean
  respectKarmaLimits: boolean
  postFrequency: string
  maxPostsPerDay: number
  pauseOnErrors: boolean
  notifyOnApproval: boolean
}

export default function RedditSettings() {
  const { projectId } = useParams()
  const { toast } = useToast()
  
  const [redditAccount, setRedditAccount] = useState<RedditAccount>({
    isConnected: false,
    karma: 0,
    accountAge: ''
  })

  const [agentSettings, setAgentSettings] = useState<AgentSettings>({
    enableRedditAgent: false,
    requiresPostApproval: true,
    autoSubscribe: true,
    respectKarmaLimits: true,
    postFrequency: 'daily',
    maxPostsPerDay: 3,
    pauseOnErrors: true,
    notifyOnApproval: true
  })

  const [isConnecting, setIsConnecting] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Load Reddit account and settings on component mount
  useEffect(() => {
    if (projectId) {
      loadRedditData()
    }
  }, [projectId])

  const loadRedditData = async () => {
    try {
      setIsLoading(true)
      
      // Check for Reddit account using generic query
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      // Use generic query to avoid TypeScript errors
      const { data: accounts, error: accountError } = await supabase
        .from('reddit_accounts' as any)
        .select('*')
        .eq('user_id', user.id)
        .eq('project_id', projectId)

      if (accountError && accountError.code !== 'PGRST116') {
        console.error('Error loading Reddit account:', accountError)
      } else if (accounts && accounts.length > 0) {
        const account = accounts[0] as any // Type assertion for Reddit account data
        setRedditAccount({
          isConnected: true,
          username: account.reddit_username || 'Unknown',
          karma: 1247, // Would need to fetch from Reddit API
          accountAge: '2 years', // Would need to calculate
          connectedAt: account.created_at,
          lastSync: account.updated_at
        })
      }

      // Load project settings
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('enable_reddit_agent, reddit_require_approval, reddit_auto_subscribe')
        .eq('id', projectId)
        .single()

      if (projectError) {
        console.error('Error loading project settings:', projectError)
      } else if (project) {
        setAgentSettings(prev => ({
          ...prev,
          enableRedditAgent: project.enable_reddit_agent || false,
          requiresPostApproval: project.reddit_require_approval !== false,
          autoSubscribe: project.reddit_auto_subscribe !== false
        }))
      }

    } catch (error) {
      console.error('Error loading Reddit data:', error)
      toast({
        title: "Error",
        description: "Failed to load Reddit settings",
        variant: "destructive"
      })
    } finally {
      setIsLoading(false)
    }
  }

  const connectReddit = async () => {
    if (!projectId) return
    
    try {
      setIsConnecting(true)
      
      // Generate OAuth URL
      const state = crypto.randomUUID()
      const clientId = import.meta.env.VITE_REDDIT_CLIENT_ID || 'your_reddit_client_id'
      const redirectUri = `${window.location.origin}/auth/reddit/callback`
      
      const authUrl = `https://www.reddit.com/api/v1/authorize?` +
        `client_id=${clientId}&` +
        `response_type=code&` +
        `state=${state}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `duration=permanent&` +
        `scope=identity,submit,read,mysubreddits`
      
      // Store state and projectId for callback verification
      localStorage.setItem('reddit_oauth_state', state)
      localStorage.setItem('reddit_oauth_project_id', projectId)
      localStorage.setItem('reddit_oauth_return_url', window.location.href)
      
      // Redirect to Reddit OAuth
      window.location.href = authUrl
      
    } catch (error) {
      console.error('Error initiating Reddit OAuth:', error)
      toast({
        title: "Error",
        description: "Failed to initiate Reddit connection",
        variant: "destructive"
      })
      setIsConnecting(false)
    }
  }

  const disconnectReddit = async () => {
    if (!projectId) return
    
    try {
      setIsDisconnecting(true)
      
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      // Delete Reddit account using generic query
      const { error } = await supabase
        .from('reddit_accounts' as any)
        .delete()
        .eq('user_id', user.id)
        .eq('project_id', projectId)

      if (error) {
        throw error
      }

      setRedditAccount({
        isConnected: false,
        karma: 0,
        accountAge: ''
      })

      toast({
        title: "Success",
        description: "Reddit account disconnected successfully"
      })

    } catch (error) {
      console.error('Error disconnecting Reddit:', error)
      toast({
        title: "Error",
        description: "Failed to disconnect Reddit account",
        variant: "destructive"
      })
    } finally {
      setIsDisconnecting(false)
    }
  }

  const syncAccount = async () => {
    try {
      setIsSyncing(true)
      
      // In a real implementation, this would fetch fresh data from Reddit API
      // For now, just simulate the sync
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      setRedditAccount(prev => ({ 
        ...prev, 
        lastSync: new Date().toISOString(),
        karma: prev.karma + Math.floor(Math.random() * 50)
      }))

      toast({
        title: "Success",
        description: "Account synced successfully"
      })

    } catch (error) {
      console.error('Error syncing account:', error)
      toast({
        title: "Error",
        description: "Failed to sync account",
        variant: "destructive"
      })
    } finally {
      setIsSyncing(false)
    }
  }

  const updateAgentSetting = async (key: keyof AgentSettings, value: any) => {
    if (!projectId) return
    
    try {
      setAgentSettings(prev => ({ ...prev, [key]: value }))

      // Map frontend settings to database columns
      const dbSettings: any = {}
      if (key === 'enableRedditAgent') {
        dbSettings.enable_reddit_agent = value
      } else if (key === 'requiresPostApproval') {
        dbSettings.reddit_require_approval = value
      } else if (key === 'autoSubscribe') {
        dbSettings.reddit_auto_subscribe = value
      }

      if (Object.keys(dbSettings).length > 0) {
        const { error } = await supabase
          .from('projects')
          .update(dbSettings)
          .eq('id', projectId)

        if (error) {
          throw error
        }

        toast({
          title: "Success",
          description: "Settings updated successfully"
        })
      }

    } catch (error) {
      console.error('Error updating settings:', error)
      toast({
        title: "Error",
        description: "Failed to update settings",
        variant: "destructive"
      })
      // Revert the setting
      setAgentSettings(prev => ({ ...prev, [key]: !value }))
    }
  }

  if (isLoading) {
    return (
      <div className="container py-6 max-w-4xl">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </div>
    )
  }

  return (
    <div className="container py-6 max-w-4xl">
      <div className="flex flex-col space-y-8">
        {/* Header */}
        <motion.div 
          className="flex items-center justify-between"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">
                Reddit Integration
              </h1>
              <p className="text-zinc-500 mt-1">
                Configure Reddit connection and AI agent settings
              </p>
            </div>
          </div>
        </motion.div>

        {/* Reddit Account Connection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card>
            <CardHeader className="bg-gradient-to-r from-zinc-50 to-white border-b border-zinc-200">
              <CardTitle className="text-xl font-semibold text-zinc-900 flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Reddit Account
              </CardTitle>
              <p className="text-sm text-zinc-500 mt-1">Manage your Reddit account connection</p>
            </CardHeader>
            <CardContent className="p-6">
              {redditAccount.isConnected ? (
                <div className="space-y-6">
                  {/* Connected Account Info */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="bg-green-100 rounded-full p-3">
                        <CheckCircle className="h-8 w-8 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-zinc-900">Connected Successfully</h3>
                        <p className="text-zinc-600">u/{redditAccount.username}</p>
                      </div>
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        Active
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-lg p-4 border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <ThumbsUp className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-zinc-700">Total Karma</span>
                        </div>
                        <p className="text-2xl font-bold text-zinc-900">{redditAccount.karma.toLocaleString()}</p>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-zinc-700">Account Age</span>
                        </div>
                        <p className="text-2xl font-bold text-zinc-900">{redditAccount.accountAge}</p>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Link className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-zinc-700">Connected</span>
                        </div>
                        <p className="text-sm text-zinc-600">
                          {redditAccount.connectedAt ? new Date(redditAccount.connectedAt).toLocaleDateString() : 'Unknown'}
                        </p>
                      </div>
                      
                      <div className="bg-white rounded-lg p-4 border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <RefreshCw className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-zinc-700">Last Sync</span>
                        </div>
                        <p className="text-sm text-zinc-600">
                          {redditAccount.lastSync ? new Date(redditAccount.lastSync).toLocaleDateString() : 'Never'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Account Actions */}
                  <div className="flex items-center gap-3">
                    <Button 
                      onClick={syncAccount}
                      disabled={isSyncing}
                      variant="outline" 
                      className="gap-2"
                    >
                      {isSyncing ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      {isSyncing ? 'Syncing...' : 'Sync Account'}
                    </Button>
                    
                    <Button 
                      onClick={disconnectReddit}
                      disabled={isDisconnecting}
                      variant="outline" 
                      className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
                    >
                      {isDisconnecting ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <Unlink className="h-4 w-4" />
                      )}
                      {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                    <Globe className="h-10 w-10 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-zinc-900 mb-3">Connect to Reddit</h3>
                  <p className="text-zinc-600 mb-6 max-w-md mx-auto">
                    Securely connect your Reddit account using OAuth2 to enable the AI agent.
                  </p>

                  {/* Check if Reddit is configured */}
                  {(() => {
                    const clientId = import.meta.env.VITE_REDDIT_CLIENT_ID
                    const isConfigured = clientId && clientId !== 'your_reddit_client_id' && clientId !== 'your_reddit_client_id_here'
                    
                    if (!isConfigured) {
                      return (
                        <div className="space-y-4">
                          <Alert className="border-amber-200 bg-amber-50 max-w-lg mx-auto text-left">
                            <AlertCircle className="h-4 w-4 text-amber-600" />
                            <AlertDescription className="text-amber-800">
                              <strong>Configuration Required:</strong> Reddit integration needs to be set up first.
                            </AlertDescription>
                          </Alert>
                          <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4 max-w-lg mx-auto text-left">
                            <h4 className="font-medium text-zinc-900 mb-2">Setup Instructions:</h4>
                            <ol className="text-sm text-zinc-600 space-y-1 list-decimal list-inside">
                              <li>Create a Reddit app at <a href="https://www.reddit.com/prefs/apps/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">reddit.com/prefs/apps</a></li>
                              <li>Set redirect URL to: <code className="bg-zinc-100 px-1 rounded">{window.location.origin}/auth/reddit/callback</code></li>
                              <li>Copy the client ID and add it to environment variables</li>
                              <li>Restart the application</li>
                            </ol>
                          </div>
                          <Button 
                            variant="outline"
                            className="gap-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-3"
                            onClick={() => window.open('https://www.reddit.com/prefs/apps/', '_blank')}
                          >
                            <ExternalLink className="h-4 w-4" />
                            Create Reddit App
                          </Button>
                        </div>
                      )
                    }
                    
                    return (
                      <Button 
                        onClick={connectReddit}
                        disabled={isConnecting}
                        className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3"
                      >
                        {isConnecting ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <ExternalLink className="h-4 w-4" />
                        )}
                        {isConnecting ? 'Connecting...' : 'Connect Reddit Account'}
                      </Button>
                    )
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* AI Agent Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card>
            <CardHeader className="bg-gradient-to-r from-zinc-50 to-white border-b border-zinc-200">
              <CardTitle className="text-xl font-semibold text-zinc-900 flex items-center gap-2">
                <Bot className="h-5 w-5" />
                AI Agent Settings
              </CardTitle>
              <p className="text-sm text-zinc-500 mt-1">Configure how the AI agent behaves</p>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Enable Reddit Agent */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 rounded-full p-2">
                    <Zap className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <Label htmlFor="enable-agent" className="text-base font-medium text-zinc-900">
                      Enable Reddit Agent
                    </Label>
                    <p className="text-sm text-zinc-600">
                      Allow the AI to automatically create and schedule Reddit posts
                    </p>
                  </div>
                </div>
                <Switch
                  id="enable-agent"
                  checked={agentSettings.enableRedditAgent}
                  onCheckedChange={(checked) => updateAgentSetting('enableRedditAgent', checked)}
                />
              </div>

              {/* Post Approval */}
              <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="bg-zinc-100 rounded-full p-2">
                    <Shield className="h-5 w-5 text-zinc-600" />
                  </div>
                  <div>
                    <Label htmlFor="require-approval" className="text-base font-medium text-zinc-900">
                      Require Post Approval
                    </Label>
                    <p className="text-sm text-zinc-600">
                      Review posts before they are published to Reddit
                    </p>
                  </div>
                </div>
                <Switch
                  id="require-approval"
                  checked={agentSettings.requiresPostApproval}
                  onCheckedChange={(checked) => updateAgentSetting('requiresPostApproval', checked)}
                />
              </div>

              {/* Auto Subscribe */}
              <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="bg-zinc-100 rounded-full p-2">
                    <Users className="h-5 w-5 text-zinc-600" />
                  </div>
                  <div>
                    <Label htmlFor="auto-subscribe" className="text-base font-medium text-zinc-900">
                      Auto Subscribe to Subreddits
                    </Label>
                    <p className="text-sm text-zinc-600">
                      Automatically subscribe to subreddits when posting
                    </p>
                  </div>
                </div>
                <Switch
                  id="auto-subscribe"
                  checked={agentSettings.autoSubscribe}
                  onCheckedChange={(checked) => updateAgentSetting('autoSubscribe', checked)}
                />
              </div>

              {/* Respect Karma Limits */}
              <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="bg-zinc-100 rounded-full p-2">
                    <Target className="h-5 w-5 text-zinc-600" />
                  </div>
                  <div>
                    <Label htmlFor="respect-karma" className="text-base font-medium text-zinc-900">
                      Respect Karma Requirements
                    </Label>
                    <p className="text-sm text-zinc-600">
                      Only post to subreddits where you meet karma requirements
                    </p>
                  </div>
                </div>
                <Switch
                  id="respect-karma"
                  checked={agentSettings.respectKarmaLimits}
                  onCheckedChange={(checked) => updateAgentSetting('respectKarmaLimits', checked)}
                />
              </div>

              <Separator />

              {/* Posting Frequency */}
              <div className="space-y-3">
                <Label className="text-base font-medium text-zinc-900 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Posting Frequency
                </Label>
                <Select 
                  value={agentSettings.postFrequency} 
                  onValueChange={(value) => updateAgentSetting('postFrequency', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Every Hour</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="custom">Custom Schedule</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Max Posts Per Day */}
              <div className="space-y-3">
                <Label className="text-base font-medium text-zinc-900 flex items-center gap-2">
                  <Brain className="h-4 w-4" />
                  Maximum Posts Per Day
                </Label>
                <Select 
                  value={agentSettings.maxPostsPerDay.toString()} 
                  onValueChange={(value) => updateAgentSetting('maxPostsPerDay', parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 post</SelectItem>
                    <SelectItem value="2">2 posts</SelectItem>
                    <SelectItem value="3">3 posts</SelectItem>
                    <SelectItem value="5">5 posts</SelectItem>
                    <SelectItem value="10">10 posts</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Status Alert */}
        {!redditAccount.isConnected && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Connect your Reddit account to enable the AI agent and start posting automatically.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  )
} 